import type { ChangeEvent } from "react";
import React, { memo, useCallback, useEffect } from "react";
import Container from "../../../../shared-components/css-modules/toyota/atoms/Container";
import Button from "../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../shared-components/css-modules/toyota/atoms/Icon";
import TextInput from "../../../../shared-components/css-modules/toyota/molecules/TextInput";
import Popover from "../../../../shared-components/css-modules/toyota/vendor/popover/Popover";
import Checkbox from "../../../../shared-components/css-modules/toyota/molecules/Checkbox";

import SearchResults from "./SearchResults";

import type { CarConfigDispatchType } from "../../../redux/store";
import { useCarConfigSelector } from "../../../redux/store";

import styles from "./Search.module.scss";
import {
    selectCarConfigSpecsSearchActiveFilters,
    selectCarConfigSpecsSearchIsSearching,
    selectCarConfigSpecsSearchValue,
    setActiveFilters,
    setSearchValue,
} from "../../../redux/slices/CarConfigSpecsSearchSlice";
import { useDebouncedEffect, useShowAfterFirstRender } from "../../../../../../common-deprecated/hooks";
import {
    trackSpecsSearch,
    trackSpecsSearchFilterClick,
    trackSpecsSearchFilterListOpen,
    trackSpecsSearchResultClick,
} from "../../../utils/tracking";
import a11y from "../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import type { SpecGroupType } from "../../../hooks/useSpecs";
import { useSpecs } from "../../../hooks/useSpecs";
import { CarConfigLabel, getCarConfigLabel } from "../../../utils/labelUtils";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const Search: React.FC = () => {
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const foundSpecs = useSpecs(true);
    const dispatch = useDispatch<CarConfigDispatchType>();
    const searchValue = useCarConfigSelector(selectCarConfigSpecsSearchValue);
    const isSearching = useCarConfigSelector(selectCarConfigSpecsSearchIsSearching);
    const activeFilters = useCarConfigSelector(selectCarConfigSpecsSearchActiveFilters);

    // Note: this search event will almost always contain all filters
    useDebouncedEffect(
        () => {
            if (isSearching) {
                trackSpecsSearch(searchValue, foundSpecs.length === 0 ? 0 : 1, activeFilters.join("|"));
            }
        },
        500,
        [searchValue, isSearching],
    );

    const active = useShowAfterFirstRender();
    useEffect(() => {
        if (active && isSearching) {
            trackSpecsSearchFilterClick(searchValue, foundSpecs.length === 0 ? 0 : 1, activeFilters.join("|"));
        }
    }, [activeFilters]);

    const onResultClick = useCallback(
        (group: SpecGroupType) => {
            trackSpecsSearchResultClick(
                searchValue,
                foundSpecs.length === 0 ? 0 : 1,
                activeFilters.join("|"),
                group.name,
            );
        },
        [activeFilters, foundSpecs.length, searchValue],
    );

    const onFilterChange = useCallback(
        (group: SpecGroupType) => () => {
            dispatch(
                setActiveFilters(
                    activeFilters.includes(group.id)
                        ? [...activeFilters.filter((item) => item !== group.id)]
                        : [...activeFilters, group.id],
                ),
            );
        },
        [dispatch, activeFilters],
    );

    const onSearchChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            dispatch(setSearchValue(event.currentTarget.value));
        },
        [dispatch],
    );

    const onClearClick = useCallback(() => {
        dispatch(setSearchValue(""));
    }, [dispatch]);

    const onPopoverChange = useCallback((isOpen: boolean) => {
        if (isOpen) {
            trackSpecsSearchFilterListOpen();
        }
    }, []);

    return (
        <Container>
            <div className={styles.wrapper}>
                <div
                    id="specs-searchfield"
                    className={`${styles.searchfield} ${searchValue ? "" : styles.closed}`}
                    role="search"
                >
                    <TextInput
                        label={getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigSpecsSearchLabel")}
                        hideLabel
                        variant="search"
                        placeholder={getCarConfigLabel(
                            { commonSettings, carConfigSettings },
                            "carConfigSpecsSearchPlaceholder",
                        )}
                        prefixIcon="search"
                        onChange={onSearchChange}
                        value={searchValue}
                    />
                    <Button
                        variant="clean"
                        onClick={onClearClick}
                        aria-controls="#specs-searchfield"
                        aria-expanded={isSearching}
                        aria-hidden={!isSearching}
                        className={styles.close}
                    >
                        <Icon variant="close" sizing="sm" aria-hidden="true" />
                        <span className={a11y["sr-only"]}>
                            <CarConfigLabel label="carConfigA11yButtonCloseSearch" />
                        </span>
                    </Button>

                    <div
                        className={`${styles["filter-search"]} ${
                            isSearching && foundSpecs.length !== 0 ? "" : styles.closed
                        }`}
                    >
                        <Popover placement="bottom" onOpenChangeListen={onPopoverChange}>
                            <Popover.Trigger>
                                <Button variant="clean">
                                    <Icon variant="filter" sizing="sm" aria-hidden="true" />
                                    <span className={a11y["sr-only"]}>
                                        <CarConfigLabel label="carConfigA11yButtonToggleFilterMenu" />
                                    </span>
                                </Button>
                            </Popover.Trigger>
                            <Popover.Content variant="filter-box">
                                <div className={styles.filterbox}>
                                    {foundSpecs.map((group) => (
                                        <Checkbox
                                            key={group.id}
                                            label={group.name}
                                            checked={activeFilters.includes(group.id)}
                                            onChange={onFilterChange(group)}
                                        />
                                    ))}
                                </div>
                            </Popover.Content>
                        </Popover>
                    </div>
                </div>

                {isSearching && <SearchResults foundSpecs={foundSpecs} onResultClick={onResultClick} />}
            </div>
        </Container>
    );
};

export default memo(Search);
